/**
 * Header Component
 * Top header bar with search, notifications, and user info.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useAuthStore } from '@store/authStore';
import { ROLE_LABELS, DEV_MOCK_USERS } from '../../config/navigation';
import { env } from '@config/env';
import { ROUTES } from '@config/constants';
import { authApi } from '@modules/registration/services/registrationService';
import { notificationsApi } from '@modules/memo/services/notificationsService';
import { useClickOutside } from '@hooks/useClickOutside';
import { MenuIcon, CloseIcon } from '@components/common/Icons';
import type { UserRole } from '@/types/common.types';
import type { Notification } from '@modules/memo/types';
import styles from './Header.module.css';

/**
 * Header component props.
 */
interface HeaderProps {
  onToggleMobileMenu: () => void;
  isMobileMenuOpen: boolean;
}

/**
 * Search icon component.
 */
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.25 14.25a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM15.75 15.75l-3.262-3.262" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Bell icon component.
 */
const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 6.667a5 5 0 1 0-10 0c0 5.833-2.5 7.5-2.5 7.5h15s-2.5-1.667-2.5-7.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.442 17.5a1.667 1.667 0 0 1-2.884 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * User avatar icon component.
 */
const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Logout icon component.
 */
const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.5 17.5H4.167A1.667 1.667 0 0 1 2.5 15.833V4.167A1.667 1.667 0 0 1 4.167 2.5H7.5M13.333 14.167 17.5 10l-4.167-4.167M17.5 10H7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Header component with user info and actions.
 */
export const Header = ({ onToggleMobileMenu, isMobileMenuOpen }: HeaderProps) => {
  const user = useUser();
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [showDevMenu, setShowDevMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationsPopup, setShowNotificationsPopup] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const notificationsPopupRef = useClickOutside<HTMLDivElement>(() =>
    setShowNotificationsPopup(false)
  );

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await notificationsApi.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Check if we're in development mode
  const isDev = env.MODE === 'development';

  /**
   * Handles switching to a different mock user (dev mode only).
   */
  const handleSwitchUser = (role: UserRole) => {
    const mockUser = DEV_MOCK_USERS[role];
    setAuth(mockUser, 'dev-token');
    setShowDevMenu(false);
  };

  /**
   * Handles logout action.
   */
  const handleLogout = async () => {
    try {
      // Call API to invalidate token on server
      await authApi.logout();
    } catch (error) {
      // Even if API call fails, continue with local logout
      console.error('Logout API error:', error);
    } finally {
      // Clear local auth state (this sets wasExplicitlyLoggedOut flag)
      useAuthStore.getState().clearAuth();
      // Remove token from localStorage
      localStorage.removeItem('dream_system_auth_token');
      // Navigate to login page
      navigate(ROUTES.LOGIN, { replace: true });
    }
  };

  const userName = user?.name || 'Guest';
  const userRole = user?.role || 'daie';
  const roleLabel = ROLE_LABELS[userRole] || "Dai'e Agent";

  const fetchNotifications = useCallback(async () => {
    const { data } = await notificationsApi.getNotifications({
      currentPage: 1,
      itemsPerPage: 10,
    });
    setNotifications(data);
  }, []);

  const handleNotificationBellClick = async () => {
    if (!showNotificationsPopup) {
      setNotificationsLoading(true);
      setShowNotificationsPopup(true);
      try {
        await fetchNotifications();
      } catch {
        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    } else {
      setShowNotificationsPopup(false);
    }
  };

  const handleNotificationClick = async (notif: Notification) => {
    const memoId = notif.memoId;
    if (notif.status === 'unread' && memoId) {
      await notificationsApi.markAsRead(memoId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, status: 'read' as const } : n))
      );
      fetchUnreadCount();
    }
    setShowNotificationsPopup(false);
    navigate(`${ROUTES.NOTIFICATIONS}?memo=${memoId ?? notif.id}`);
  };

  const handleReadAll = async () => {
    await notificationsApi.markAllAsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' as const })));
    setUnreadCount(0);
    fetchUnreadCount();
  };

  useEffect(() => {
    const onNotificationRead = () => {
      fetchUnreadCount();
      if (showNotificationsPopup) fetchNotifications();
    };
    window.addEventListener('notification-read', onNotificationRead);
    return () => window.removeEventListener('notification-read', onNotificationRead);
  }, [showNotificationsPopup, fetchUnreadCount, fetchNotifications]);

  const formatNotificationDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const stripHtmlPreview = (html: string) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const hasUnread = notifications.some((n) => n.status === 'unread');

  return (
    <header className={styles.header}>
      {/* Mobile Menu Toggle Button */}
      <button
        className={styles.mobileMenuButton}
        onClick={onToggleMobileMenu}
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isMobileMenuOpen}
        type="button"
      >
        {isMobileMenuOpen ? (
          <CloseIcon size={24} color="currentColor" />
        ) : (
          <MenuIcon size={24} color="currentColor" />
        )}
      </button>

      {/* Search Bar */}
      <div className={styles.searchContainer}>
        <SearchIcon />
        <input
          type="text"
          placeholder="Search..."
          className={styles.searchInput}
          aria-label="Search"
        />
      </div>

      {/* Right Section */}
      <div className={styles.rightSection}>
        {/* Notifications */}
        <div className={styles.notificationWrapper} ref={notificationsPopupRef}>
          <button
            type="button"
            className={styles.notificationButton}
            aria-label="Notifications"
            aria-expanded={showNotificationsPopup}
            onClick={handleNotificationBellClick}
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span className={styles.notificationBadge}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {showNotificationsPopup && (
            <div className={styles.notificationPopup}>
              <div className={styles.notificationPopupHeader}>
                <span>Notifications</span>
                {hasUnread && (
                  <button
                    type="button"
                    className={styles.readAllButton}
                    onClick={handleReadAll}
                  >
                    Read All
                  </button>
                )}
              </div>
              <div className={styles.notificationList}>
                {notificationsLoading ? (
                  <div className={styles.notificationEmpty}>
                    Loading...
                  </div>
                ) : notifications.length === 0 ? (
                  <div className={styles.notificationEmpty}>
                    No notifications
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <button
                      key={notif.id}
                      type="button"
                      className={`${styles.notificationItem} ${notif.status === 'unread' ? styles.notificationItemUnread : ''}`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <span className={styles.notificationItemTitle}>
                        {notif.title}
                      </span>
                      <span className={styles.notificationItemPreview}>
                        {stripHtmlPreview(notif.preview)}
                      </span>
                      <span className={styles.notificationItemDate}>
                        {formatNotificationDate(notif.sentAt)}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Info */}
        <div className={styles.userSection}>
          <div className={styles.userInfo}>
            <span className={styles.userName}>{userName}</span>
            <span className={styles.userRole}>{roleLabel}</span>
          </div>
          <div className={styles.userAvatar}>
            <UserIcon />
          </div>
        </div>

        {/* Logout Button */}
        <button
          className={styles.logoutButton}
          onClick={handleLogout}
          aria-label="Logout"
          title="Logout"
        >
          <LogoutIcon />
        </button>

        {/* Dev Mode User Switcher */}
        {isDev && (
          <div className={styles.devSwitcher}>
            <button
              className={styles.devButton}
              onClick={() => setShowDevMenu(!showDevMenu)}
              title="Switch User (Dev Mode)"
            >
              DEV
            </button>
            {showDevMenu && (
              <div className={styles.devMenu}>
                <span className={styles.devMenuTitle}>Switch User</span>
                <button
                  className={`${styles.devMenuItem} ${userRole === 'daie' ? styles.devMenuItemActive : ''}`}
                  onClick={() => handleSwitchUser('daie')}
                >
                  Daie
                </button>
                <button
                  className={`${styles.devMenuItem} ${userRole === 'manager' ? styles.devMenuItemActive : ''}`}
                  onClick={() => handleSwitchUser('manager')}
                >
                  Manager
                </button>
                <button
                  className={`${styles.devMenuItem} ${userRole === 'admin' ? styles.devMenuItemActive : ''}`}
                  onClick={() => handleSwitchUser('admin')}
                >
                  Admin
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

